/**
 * localStorage-only "Recents" list: the last MAX_RECENTS successful report
 * generations (reportType + requestParams), auto-captured at the same seam
 * as Save configuration (ReportsPage.handleGenerateReport and
 * lib/generateReport.runSavedReportGeneration). No backend - purely a
 * client-side convenience list, rendered under the Saved rail.
 */
const STORAGE_KEY = 'banyan_recent_reports';
const MAX_RECENTS = 10;

// Fired on window after a successful capture so any mounted rail can refetch
// without prop-drilling a refresh callback across the tree (a report can be
// generated from a popup on ReportsPage while the rail listens elsewhere).
export const RECENT_REPORTS_CHANGED_EVENT = 'banyan:recent-reports-changed';

function dedupeKey(reportType, requestParams) {
  const params = requestParams || {};
  const sorted = Object.keys(params)
    .sort()
    .map((k) => `${k}:${JSON.stringify(params[k])}`)
    .join('|');
  return `${reportType}::${sorted}`;
}

// Recents retire daily (anti-bloat, owner directive 2026-07-17): an entry
// generated on a prior calendar day (local clock) is dropped on the next
// read, so the rail only ever shows today's generations.
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/** The stored recents list, most recent first, pruned to TODAY's entries.
 * Never throws. */
export function getRecentReports() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const midnight = startOfToday();
    const fresh = parsed.filter((r) => {
      const t = Date.parse(r && r.generatedAt);
      return Number.isFinite(t) && t >= midnight;
    });
    if (fresh.length !== parsed.length) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
      } catch (_) { /* best effort */ }
    }
    return fresh;
  } catch (_) {
    return [];
  }
}

/**
 * Record a successful generation. De-dupes by (reportType, requestParams) -
 * re-running the same config bumps it to the front instead of duplicating.
 * Caps at MAX_RECENTS, oldest dropped.
 */
export function addRecentReport({ reportType, reportName, requestParams }) {
  if (!reportType) return;
  const key = dedupeKey(reportType, requestParams);
  const existing = getRecentReports().filter((r) => dedupeKey(r.reportType, r.requestParams) !== key);
  const next = [
    {
      reportType,
      reportName: reportName || reportType,
      requestParams: requestParams || {},
      generatedAt: new Date().toISOString(),
    },
    ...existing,
  ].slice(0, MAX_RECENTS);
  persist(next);
}

/**
 * Drop one recent by its (reportType, requestParams) identity - NOT by index:
 * the daily prune on read can shift indices between render and click.
 */
export function removeRecentReport({ reportType, requestParams }) {
  const key = dedupeKey(reportType, requestParams);
  const kept = getRecentReports().filter(
    (r) => dedupeKey(r.reportType, r.requestParams) !== key,
  );
  persist(kept);
}

function persist(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (_) {
    return; // storage full/unavailable - best effort only
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(RECENT_REPORTS_CHANGED_EVENT));
  }
}
